<template>
  <div class="index-cmd" @keydown.escape="$emit('blur-search')">
    <div class="index-cmd__row">
      <QuickSearchField
        v-if="showSearch"
        ref="searchFieldRef"
        :model-value="search"
        :placeholder="resolvedPlaceholder"
        @update:model-value="$emit('update:search', $event ?? '')"
        @keydown.enter="$emit('search')"
      />

      <slot name="after-search" />

      <v-btn
        v-if="showAdvancedButton"
        class="index-cmd__advanced-toggle"
        :variant="advOpen ? 'flat' : 'tonal'"
        color="primary"
        size="small"
        prepend-icon="mdi-filter-variant"
        :aria-expanded="String(advOpen)"
        aria-controls="index-command-advanced"
        @click.stop="toggleAdvanced"
      >
        Advanced Search
      </v-btn>

      <v-spacer />

      <div class="index-cmd__tools">
        <slot name="before-tools" />

        <v-menu v-if="showColumnVisibility && columns.length" :close-on-content-click="false">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="tonal"
              size="small"
              prepend-icon="mdi-view-column"
            >
              Column visibility
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item
              v-for="col in columns"
              :key="colKey(col)"
              :title="colTitle(col)"
              @click="$emit('toggle-column', colKey(col))"
            >
              <template #prepend>
                <v-checkbox-btn :model-value="isColumnVisible(col)" />
              </template>
            </v-list-item>
          </v-list>
        </v-menu>

        <template v-if="showExports">
          <v-btn variant="tonal" size="small" prepend-icon="mdi-content-copy" @click="$emit('copy')">
            Copy
          </v-btn>
          <v-btn variant="tonal" size="small" prepend-icon="mdi-file-excel" @click="$emit('excel')">
            Excel
          </v-btn>
          <v-btn variant="tonal" size="small" prepend-icon="mdi-file-pdf-box" @click="$emit('pdf')">
            PDF
          </v-btn>
          <v-btn variant="tonal" size="small" prepend-icon="mdi-printer" @click="$emit('print')">
            Print
          </v-btn>
        </template>

        <slot name="tools" />
      </div>
    </div>

    <v-expand-transition>
      <div
        v-if="advOpen && (searchOptions.length || $slots.advanced)"
        id="index-command-advanced"
        class="index-cmd__advanced"
      >
        <slot name="advanced">
          <div class="index-cmd__advanced-title">Advanced Search</div>
          <v-row dense>
            <template v-for="opt in searchOptions" :key="opt.name">
              <v-col
                v-if="opt.type !== 'info'"
                cols="12"
                :md="opt.cols >= 12 ? 12 : (opt.cols >= 6 ? 6 : 3)"
              >
                <template v-if="opt.type === 'date_range' || opt.type === 'month_range'">
                  <div class="index-cmd__opt-label">{{ opt.label }}</div>
                  <div class="d-flex align-center ga-2">
                    <v-text-field
                      :model-value="draft[opt.name]?.start"
                      type="date"
                      label="From"
                      density="compact"
                      variant="outlined"
                      hide-details
                      clearable
                      @update:model-value="(v) => patchRange(opt.name, 'start', v)"
                    />
                    <v-text-field
                      :model-value="draft[opt.name]?.end"
                      type="date"
                      label="To"
                      density="compact"
                      variant="outlined"
                      hide-details
                      clearable
                      @update:model-value="(v) => patchRange(opt.name, 'end', v)"
                    />
                  </div>
                </template>
                <v-select
                  v-else-if="isSelectType(opt.type)"
                  :model-value="draft[opt.name]"
                  :items="opt.choices || []"
                  item-title="label"
                  item-value="value"
                  :label="opt.label"
                  :multiple="opt.type === 'multi-select' || opt.multiple"
                  :chips="opt.type === 'multi-select' || opt.multiple"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:model-value="(v) => setDraft(opt.name, v)"
                />
                <v-checkbox
                  v-else-if="opt.type === 'checkbox'"
                  :model-value="!!draft[opt.name]"
                  :label="opt.optionLabel || opt.label"
                  density="compact"
                  hide-details
                  @update:model-value="(v) => setDraft(opt.name, v ? '1' : null)"
                />
                <v-textarea
                  v-else-if="opt.type === 'text'"
                  :model-value="draft[opt.name]"
                  :label="opt.label"
                  :placeholder="opt.placeholder || ''"
                  density="compact"
                  variant="outlined"
                  hide-details
                  rows="2"
                  clearable
                  @update:model-value="(v) => setDraft(opt.name, v)"
                />
                <v-text-field
                  v-else-if="opt.type === 'date'"
                  :model-value="draft[opt.name]"
                  type="date"
                  :label="opt.label"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:model-value="(v) => setDraft(opt.name, v)"
                />
                <v-text-field
                  v-else-if="opt.type === 'number'"
                  :model-value="draft[opt.name]"
                  type="number"
                  :label="opt.label"
                  :placeholder="opt.placeholder || ''"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:model-value="(v) => setDraft(opt.name, v)"
                />
                <v-text-field
                  v-else
                  :model-value="draft[opt.name]"
                  :label="opt.label"
                  :placeholder="opt.placeholder || ''"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:model-value="(v) => setDraft(opt.name, v)"
                />
              </v-col>
            </template>
          </v-row>
          <div class="d-flex flex-wrap ga-2 mt-3">
            <v-btn color="primary" variant="flat" size="small" @click="applyAdvanced">
              Search
            </v-btn>
            <v-btn variant="text" size="small" @click="resetAdvanced">
              Reset
            </v-btn>
          </div>
        </slot>
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, useSlots, watch } from 'vue'
import QuickSearchField from '@/shared/components/QuickSearchField.vue'

const props = defineProps({
  search: { type: String, default: '' },
  options: { type: Object, default: () => ({}) },
  searchOptions: { type: Array, default: () => [] },
  columns: { type: Array, default: () => [] },
  /** LMS style: key => false means hidden. Ignored when hiddenColumns is set. */
  columnVisibility: { type: Object, default: () => ({}) },
  /** Alternate: key => true means hidden (used by most non-LMS modules). */
  hiddenColumns: { type: Object, default: null },
  pluralTitle: { type: String, default: 'records' },
  placeholder: { type: String, default: '' },
  showSearch: { type: Boolean, default: true },
  showExports: { type: Boolean, default: true },
  showColumnVisibility: { type: Boolean, default: true },
  /** Force advanced button even without searchOptions (use #advanced slot). */
  showAdvanced: { type: Boolean, default: null },
  /** Controlled advanced open state (optional). */
  advancedOpen: { type: Boolean, default: undefined },
})

const emit = defineEmits([
  'update:search',
  'update:advancedOpen',
  'search',
  'apply-options',
  'toggle-column',
  'copy',
  'excel',
  'pdf',
  'print',
  'blur-search',
  'toggle-advanced',
])

const slots = useSlots()
const internalAdvOpen = ref(false)
const draft = reactive({})
const searchFieldRef = ref(null)

const advOpen = computed({
  get () {
    return props.advancedOpen !== undefined ? props.advancedOpen : internalAdvOpen.value
  },
  set (v) {
    if (props.advancedOpen !== undefined) emit('update:advancedOpen', v)
    else internalAdvOpen.value = v
  },
})

const resolvedPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder
  return `Search ${props.pluralTitle}…`
})

const showAdvancedButton = computed(() => {
  if (props.showAdvanced != null) return props.showAdvanced
  return (props.searchOptions || []).length > 0 || !!slots.advanced
})

function colKey (col) {
  return col.key ?? col.name ?? col.value
}

function colTitle (col) {
  return col.title ?? col.label ?? String(colKey(col) || '')
}

function isColumnVisible (col) {
  const key = colKey(col)
  if (props.hiddenColumns) return !props.hiddenColumns[key]
  return props.columnVisibility[key] !== false
}

function isSelectType (type) {
  return ['select', 'select2', 'nested_select', 'multi-select'].includes(type)
}

function emptyDraft () {
  const next = {}
  ;(props.searchOptions || []).forEach((opt) => {
    if (opt.type === 'date_range' || opt.type === 'month_range') {
      next[opt.name] = { start: '', end: '' }
    } else if (opt.type === 'multi-select') {
      next[opt.name] = []
    } else if (opt.type === 'checkbox') {
      next[opt.name] = null
    } else {
      next[opt.name] = ''
    }
  })
  return next
}

function syncDraftFromOptions () {
  const base = emptyDraft()
  Object.keys(base).forEach((key) => {
    const val = props.options?.[key]
    if (val == null || val === '') return
    if (typeof base[key] === 'object' && !Array.isArray(base[key]) && typeof val === 'object') {
      base[key] = { start: val.start || '', end: val.end || '' }
    } else {
      base[key] = val
    }
  })
  Object.keys(draft).forEach((k) => delete draft[k])
  Object.assign(draft, base)
}

function setDraft (name, value) {
  draft[name] = value
}

function patchRange (name, key, value) {
  draft[name] = { ...(draft[name] || {}), [key]: value }
}

function toggleAdvanced () {
  if (!advOpen.value && props.searchOptions.length) syncDraftFromOptions()
  advOpen.value = !advOpen.value
  emit('toggle-advanced', advOpen.value)
}

function sanitizeDraft () {
  const next = {}
  Object.entries(draft).forEach(([key, val]) => {
    if (val == null || val === '') return
    if (Array.isArray(val)) {
      if (val.length) next[key] = val
      return
    }
    if (typeof val === 'object') {
      const start = val.start || ''
      const end = val.end || ''
      if (start || end) next[key] = { start, end }
      return
    }
    next[key] = val
  })
  return next
}

function applyAdvanced () {
  emit('apply-options', sanitizeDraft())
}

function resetAdvanced () {
  Object.assign(draft, emptyDraft())
  emit('apply-options', {})
}

watch(
  () => props.options,
  () => {
    if (advOpen.value && props.searchOptions.length) syncDraftFromOptions()
  },
  { deep: true }
)

function onSlash (e) {
  if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
  const tag = (e.target?.tagName || '').toLowerCase()
  if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return
  e.preventDefault()
  searchFieldRef.value?.focus()
}

onMounted(() => {
  if (props.searchOptions.length) syncDraftFromOptions()
  window.addEventListener('keydown', onSlash)
})
onUnmounted(() => window.removeEventListener('keydown', onSlash))

defineExpose({
  focus: () => searchFieldRef.value?.focus(),
  openAdvanced: () => {
    if (props.searchOptions.length) syncDraftFromOptions()
    advOpen.value = true
  },
})
</script>

<style src="../styles/index-page-panel.scss"></style>
